import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export function NotificationsBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const readAll = useMutation({
    mutationFn: async () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readOne = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = data?.unread || 0;
  const items = data?.items || [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-patria-sage" data-testid="notifications-bell">
          <Bell className="h-5 w-5 text-patria-greenDeep" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-count"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-patria-terracotta text-patria-surface text-[10px] font-semibold flex items-center justify-center"
            >
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-patria-surface border-patria-muted/20 p-0" data-testid="notifications-menu">
        <div className="flex items-center justify-between px-4 py-3 border-b border-patria-muted/15">
          <span className="font-heading font-semibold text-patria-ink">Notifications</span>
          {unread > 0 && (
            <button
              onClick={() => readAll.mutate()}
              className="inline-flex items-center gap-1 text-xs text-patria-terracottaDeep hover:text-patria-terracotta"
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-patria-muted">You're all caught up.</p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-${n.id}`}
              onClick={() => {
                readOne.mutate(n.id);
                if (n.case_id) navigate(`/cases/${n.case_id}`);
              }}
              className={cn(
                "w-full text-left px-4 py-3 border-b border-patria-muted/10 hover:bg-patria-sage/50 transition-colors",
                !n.read && "bg-patria-terracotta/5"
              )}
            >
              <div className="flex gap-2">
                {!n.read && <span className="h-2 w-2 rounded-full bg-patria-terracotta mt-1.5 shrink-0" />}
                <div className={cn(!n.read ? "" : "pl-4")}>
                  <p className="text-sm text-patria-ink leading-snug">{n.message}</p>
                  <p className="text-[11px] text-patria-muted mt-0.5">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
